import { Inject, Injectable } from '@angular/core';

import { ILoggingService } from '../services/interfaces/ILoggingService';

import { Api } from './api';

@Injectable({
    providedIn: 'root'
})
export class MapApi {
    constructor(private readonly api: Api, @Inject('LoggingService') public loggingService: ILoggingService) { }

    mapProperties: string = 'id, extentJSON, name, uniqueName, description, data, isSystem, createdDate, lastEditedDate, isPublic, showOnAoi, basemap, categories{id, name}, layers{id, parentId, name}, mapCategories{id, name}, users{id, firstName, lastName}';

    async deleteMap(id: number) {
        return await this.api.http2.graphqlRequest(`mutation {
            deleteMap(id: ${id})
        }`, null, 'GET', null);
    }

    async deleteMaps(ids: number[]) {
        return await this.api.http2.graphqlRequest(`
            mutation deleteMaps($ids: [Int]) {
                deleteMaps(ids: $ids)
            }
        `, { ids }, 'GET', 'deleteMaps');
    }

    async getMaps(id: number[], properties: string = null) {
        const props = properties ? properties : this.mapProperties;
        return await this.api.http2.graphqlRequest(`query GetMaps($id: [Int]) {
                maps(id: $id){
                    ${props}
                }
            }`, { id }, 'GET', 'maps');
    }

    async getSystemMaps(applicationId: number, properties?: string) {
        const props = properties ? properties : this.mapProperties;
        return await this.api.http2.graphqlRequest(`query {
            applications(id: ${applicationId}) {
                maps(isSystem: true) {
                    ${props}
                }
            }
        }`, null, 'GET', 'applications');
    }

    async getUserMaps(id: number, properties: string) {
        return await this.api.http2.graphqlRequest(`query GetUserMaps($id: [Int]) {
            users(id: $id) {
                ${properties}
            }
        }`, { id }, 'GET', 'users');
    }

    async getUserMap(userId: number, mapId: number, applicationId?: number) {
        let appIdFilter = '';
        if (applicationId) {
            appIdFilter = `, applicationId:${applicationId}`;
        }
        return await this.api.http2.graphqlRequest(`query GetUserMaps($userId: [Int], $mapId: [Int]) {
            users(id: $userId) {
                firstName, lastName, email, userMaps(mapId: $mapId${appIdFilter}) {
                    map(isSystem: false) {
                        id, name, description, data, isSystem, isPublic, createdDate, lastEditedDate, basemap, userMaps {
                            userId, accessLevel }}}
            }
        }`, { userId, mapId }, 'GET', 'users');
    }

    async getUserMapsFolders(id: number, properties: string) {
        return await this.api.http2.graphqlRequest(`query getUserMapsFolders($id: [Int]) {
                users(id: $id) {
                    ${properties}
                }
            }`, { id }, 'GET', 'users');
    }

    async getMapSearch(searchText: string) {
        return await this.api.http2.graphqlRequest(`
                query MapSearch($searchText: [String]) {
                    mapCatalog(search: $searchText){
                        mapId
                        primaryDisplayText
                        secondaryDisplayText

                    }
            }`, { searchText }, 'GET', 'mapCatalog');
    }

    async shareMap(userIds: number[], mapId: number, emailSubject: string = '', emailBody: string = '') {
        return await this.api.http2.graphqlRequest(`
                mutation ShareMap($userIds: [Int], $mapId: Int, $emailSubject: String, $emailBody: String) {
                    shareMap(userIds: $userIds, mapId: $mapId, emailSubject: $emailSubject, emailBody: $emailBody)
                }
        `, { userIds, mapId, emailSubject, emailBody }, 'GET', 'shareMap');
    }

    async shareMaps(userIds: number[], mapIds: number[], emailSubject: string = '', emailBody: string = '') {
        return await this.api.http2.graphqlRequest(`
                mutation ShareMaps($userIds: [Int], $mapIds: [Int], $emailSubject: String, $emailBody: String) {
                    shareMaps(userIds: $userIds, mapIds: $mapIds, emailSubject: $emailSubject, emailBody: $emailBody)
                }
        `, { userIds, mapIds, emailSubject, emailBody }, 'GET', 'shareMaps');
    }

    async unShareMap(userIds: number[], mapId: number) {
        return await this.api.http2.graphqlRequest(`
                mutation unshareMap($userIds: [Int], $mapId: Int) {
                    unshareMap(userIds: $userIds, mapId: $mapId)
                }
        `, { userIds, mapId }, 'GET', 'unshareMap');
    }

    async unShareMaps(userIds: number[], mapIds: number[]) {
        return await this.api.http2.graphqlRequest(`
                mutation unshareMaps($userIds: [Int], $mapIds: [Int]) {
                    unshareMaps(userIds: $userIds, mapIds: $mapIds)
                }
        `, { userIds, mapIds }, 'GET', 'unshareMaps');
    }

    async saveMap(map, layers, applicationId = 0) {
        if (!map.id) {
            delete map.id;
        }
        return await this.api.http2.graphqlRequest(`
            mutation SaveMap($map: MapInput!, $layers: [LayerInput!], $applicationId: Int){
                saveMap(map: $map, layers: $layers, applicationId: $applicationId){
                    ${this.mapProperties}
                }
            }
        `, { map: this.sanitize(map), layers, applicationId }, 'POST', 'saveMap');
    }

    async saveMapToUserFolder(mapId, folderId, oldFolderId?) {
        return await this.api.http2.graphqlRequest(`
          mutation SaveMapToUserFolder($mapId: Int, $folderId: Int, $oldFolderId: Int) {
              saveMapToUserFolder(newUserFolderId: $folderId, mapId: $mapId, oldUserFolderId: $oldFolderId)
          }
        `, { mapId, folderId, oldFolderId }, 'POST', 'saveMapToUserFolder');
    }

    async copyMaps(mapIds: number[], appId: number) {
        const props = this.mapProperties;
        return await this.api.http2.graphqlRequest(`
            mutation CopyMaps($mapIds: [Int], $appId: Int) {
                copyMaps(mapIds: $mapIds, appId: $appId){
                    ${props}
                }
            }
        `, { mapIds, appId }, 'POST', 'copyMaps');
    }

    // async update(map) {
    //     return await this.api.http2.graphqlRequest(`
    //         mutation UpdateMap($map: MapInput!){
    //             saveMap(map: $map){
    //                 title
    //             }
    //         }
    //     `, { map: this.sanitize(map) }, 'POST', 'saveMap');
    // }

    sanitize(map) {
        const result = this.api.pick(map, this.mapProperties.split(', '));
        return result;
    }

    public async setTokenCookie(token: string, url: string): Promise<string | Event> {
        return this.api.setCookieInIFrame(token, url);
    }

    // async delete(mapId: number) => {
    //     return await this.api.http2.graphqlRequest(`
    //         mutation Deletemap($mapId: Int!) {
    //             deleteMap(id: $MapId)
    //         }
    //     `, { mapId });
    // }
}
